import React, { useEffect, useState } from 'react';
import OlcButton from '~/components/General/OlcButton';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Action
import {
  changeDefaultPaymentMethod,
  detachPaymentMethod,
  getAllPaymentMethods,
} from '~/redux/actions/stripe-actions';
import { failure, success } from '~/redux/actions/snackbar-actions';
import { getUserData } from '~/redux/actions/auth-actions';

// Utils
import { ROLES } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';

// Components
import Stripe from '~/components//Stripe/Stripe';
import ConfirmModal from '~/components/General/ConfirmModal';
import PaymentMethod from '../PaymentMethod';

// MUI Components
import { Box, Typography, IconButton, Tooltip, CircularProgress, Skeleton } from '@mui/material';

// Icons
import {
  PaymentTrashCIcon,
  VisaCIcon,
  BankCIcon,
  MasterCardCIcon,
  CreditCardFallbackCIcon,
  PaymentMethodDelIcon,
  ModalTrashIcon,
} from '~/assets/images';
import AddIcon from '@mui/icons-material/Add';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';

import './styles.scss';

const paymentIcons = {
  visa: VisaCIcon,
  us_bank_account: BankCIcon,
  mastercard: MasterCardCIcon,
};

const PaymentMethodBoard = ({ openPaymentMethodModal = () => {} }) => {
  const dispatch = useDispatch();
  const [dialog, setDialog] = useState({
    open: false,
    message: '',
    type: '',
    id: '',
    loading: false,
    isConfirmDialog: false,
    header: '',
    icon: null,
    successButtonName: 'Yes',
    cancelButtonName: 'No',
  });
  const [isDeleteError, setIsDeleteError] = useState(false);
  const [loading, setLoading] = useState({
    id: null,
    state: false,
  });

  const user = useSelector((state) => state.userReducers?.user);
  const isUser = !(user?.role?.slug === ROLES['ORGANIZATION_ADMIN']);
  const paymentMethods = useSelector((state) => state.stripeReducers?.methods);
  const isPaymentMethodLoading = useSelector((state) => state.stripeReducers?.areMethodsLoading);
  const numCards = (paymentMethods || []).filter((m) => m?.type === 'card').length;

  const defaultIndex = paymentMethods.findIndex(
    (item) => user?.organization?.defaultPaymentMethodStripeId === item?.id
  );

  if (defaultIndex !== -1 && defaultIndex !== 0) {
    paymentMethods.unshift(paymentMethods.splice(defaultIndex, 1)[0]);
  }

  const handleGetAllMethods = () => {
    dispatch(getAllPaymentMethods());
  };

  const handleDelete = async (id) => {
    const data = await detachPaymentMethod(id);
    if (data.status === 200) {
      dispatch(success(MESSAGES.ACCOUNT.WALLET.STRIPE.METHOD_DELETE_SUCCESS, ' '));
      handleGetAllMethods();
    } else {
      if (paymentMethods.length === 1) {
        setIsDeleteError(true);
      } else {
        dispatch(failure(data.data.message, ' '));
      }
    }
    handleCloseConfirmDialog();
  };

  const handleChangeDefaultMethod = async (id) => {
    setLoading({ id: id, state: true });
    const data = await changeDefaultPaymentMethod(id);
    if (data.status === 200) {
      dispatch(success(MESSAGES.ACCOUNT.WALLET.STRIPE.DEFAULT_CHANGE_SUCCESS, ' '));
      dispatch(getUserData());
      handleGetAllMethods();
    } else {
      dispatch(failure(MESSAGES.ACCOUNT.WALLET.STRIPE.DEFAULT_CHANGE_ERROR, ' '));
    }
    setLoading({ id: null, state: false });
    handleCloseConfirmDialog();
  };

  const handleCloseConfirmDialog = () => {
    setDialog({
      open: false,
      message: '',
      type: '',
      id: '',
      loading: false,
      isConfirmDialog: false,
      header: '',
      icon: null,
      successButtonName: 'Yes',
      cancelButtonName: 'No',
    });
  };

  const handleConfirmDialog = () => {
    setDialog((prev) => ({ ...prev, loading: true }));
    if (dialog.type === 'delete') {
      handleDelete(dialog.id);
    } else {
      handleChangeDefaultMethod(dialog.id);
    }
  };

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    handleGetAllMethods();
  }, [user?.id]);

  return (
    <>
      <Box className="internalBox PaymentMethodsSection pmBox">
        <Box className="internalBoxHeader">
          <Box>
            <CreditCardOutlinedIcon className="walletCardHeaderIcon" />
          </Box>
          <Box>
            <Typography className="internalBoxHeaderTitle">Payment Methods</Typography>
          </Box>
          <Box className="headerActionBtn">
            {isPaymentMethodLoading ? (
              <Skeleton
                variant="rectangular"
                width={200}
                height={40}
                sx={{ borderRadius: '8px' }}
              />
            ) : (
              numCards < 3 && (
                <Tooltip
                  title={
                    isUser ? MESSAGES.ACCOUNT.WALLET.STRIPE.UNAUTHORIZED_ADD_PAYMENT_METHOD : ''
                  }
                >
                  <OlcButton
                    variant="primary"
                    className="olcPrimaryBtnPayment"
                    onClick={() => {
                      if (isUser) return;
                      openPaymentMethodModal();
                    }}
                    disabled={isUser}
                  >
                    <AddIcon /> Add Payment Method
                  </OlcButton>
                </Tooltip>
              )
            )}
          </Box>
        </Box>

        <Box sx={{ marginTop: 'auto' }}>
          {isPaymentMethodLoading && (
            <Box>
              <Box className="olcpaymentMethodBar">
                <Box className="numberWrapper">
                  <Skeleton variant="text" width={200} height={20} />
                  <Skeleton variant="rectangular" width={31} height={20} />
                </Box>
                <Box className="iconsWrapperPayment">
                  <Box>
                    <Skeleton variant="circular" width={24} height={24} />
                  </Box>
                  <Box>
                    <Skeleton variant="circular" width={24} height={24} />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
          {!isPaymentMethodLoading && paymentMethods.length === 0 && (
            <Box className="noPaymentMethodTextOnBoard">
              <Typography>No payment methods added</Typography>
            </Box>
          )}
          {!isPaymentMethodLoading && (
            <>
              <Box>
                {paymentMethods.map((item, i) => {
                  const isDefaultPaymentMethod =
                    user?.organization?.defaultPaymentMethodStripeId === item?.id;

                  return (
                    <Box
                      key={item.id || i}
                      className={`olcpaymentMethodBar ${
                        isDefaultPaymentMethod ? 'isDefaultPaymentMethod' : ''
                      }`}
                    >
                      <Box className="numberWrapper">
                        <Typography>
                          {item.type === 'card' ? '**** **** **** ' : '******'}
                          {item.details.last4}
                        </Typography>
                        <Box className="paymentMethodMeta">
                          <Box style={{ width: '31px', height: '20px' }}>
                            <img
                              src={
                                item.type === 'card'
                                  ? paymentIcons[item.details.brand] || CreditCardFallbackCIcon
                                  : paymentIcons[item.type]
                              }
                              alt={`${item.details.brand || item.type} payment method`}
                              style={{ width: '31px', height: '20px' }}
                            />
                          </Box>
                          {isDefaultPaymentMethod && (
                            <Typography component="span" className="defaultPaymentBadge">
                              <StarIcon aria-hidden="true" />
                              Default
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box className="iconsWrapperPayment">
                        <Box>
                          {loading.id === item.id && loading.state ? (
                            <Box
                              sx={{
                                padding: '4px 8px',
                              }}
                            >
                              <CircularProgress
                                sx={{
                                  width: '17px !important',
                                  height: '17px !important',
                                  color: '#ED5C2F',
                                }}
                              />
                            </Box>
                          ) : isDefaultPaymentMethod ? (
                            <Tooltip title="Default payment method">
                              <Box
                                className="defaultPaymentIcon"
                                role="img"
                                aria-label="Default payment method"
                              >
                                <StarIcon aria-hidden="true" />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Tooltip
                              title={
                                isUser
                                  ? 'Only organization admins can change the default payment method'
                                  : 'Set as default payment method'
                              }
                            >
                              <span>
                                <IconButton
                                  className="favBtn"
                                  disabled={isUser}
                                  aria-label={`Set payment method ending in ${item.details.last4} as default`}
                                  onClick={() => handleChangeDefaultMethod(item.id)}
                                >
                                  <StarBorderIcon aria-hidden="true" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                        <Box>
                          <Tooltip
                            title={
                              isUser
                                ? MESSAGES.ACCOUNT.WALLET.STRIPE.UNAUTHORIZED_DELETE_PAYMENT_METHOD
                                : isDefaultPaymentMethod
                                  ? 'Default payment method cannot be deleted'
                                  : paymentMethods.length === 1
                                    ? 'At least one payment method is required'
                                    : ''
                            }
                          >
                            <IconButton
                              className="favBtn delBtnWallet"
                              disabled={
                                isUser || isDefaultPaymentMethod || paymentMethods.length === 1
                              }
                              onClick={() => {
                                if (isUser || isDefaultPaymentMethod || paymentMethods.length === 1)
                                  return;
                                setDialog({
                                  open: true,
                                  id: item.id,
                                  message: 'Are you sure you want to delete this payment method?',
                                  type: 'delete',
                                  header: 'Delete Payment Method',
                                  icon: ModalTrashIcon,
                                  successButtonName: 'Yes',
                                  cancelButtonName: 'No',
                                });
                              }}
                            >
                              <img
                                src={PaymentMethodDelIcon}
                                style={{ width: '24px', height: '24px' }}
                                alt="Trash"
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              {isDeleteError && (
                <Typography className="paymentMethod__error">
                  {MESSAGES.ACCOUNT.WALLET.STRIPE.PAYMENT_METHOD_ERROR}
                </Typography>
              )}
            </>
          )}
        </Box>

        {dialog.open && (
          <ConfirmModal
            openConfirmModal={{
              open: dialog.open,
              message: dialog.message,
              header: dialog.header,
              icon: dialog.icon,
              loading: dialog.loading,
              successButtonName: dialog.successButtonName,
              cancelButtonName: dialog.cancelButtonName,
              handleOk: handleConfirmDialog,
              handleClose: handleCloseConfirmDialog,
            }}
          />
        )}
      </Box>
    </>
  );
};

export default PaymentMethodBoard;
